"use client"

import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { FIELD_UNDERLINE, FieldShell } from "@/components/graphite/field"
import { PageAction } from "@/components/graphite/page-header"
import { QuietAction } from "@/components/graphite/surface"
import { cn } from "@/lib/utils"
import { Upload, FileText, Edit2, Trash2 } from 'lucide-react'
import { useDeleteFilesOwnerFileId, useGetFilesOwnerFileIdPresign, usePostFilesOwnerOwnerId, usePutFilesFileIdRename } from "openapi/api/endpoints"
import { toast } from "sonner"
import { FileMetadata } from "openapi/api/model"
import { useQueryClient } from "@tanstack/react-query"


type JobFile = FileMetadata

interface JobFileUploadProps {
    jobId: string
}

export function JobFileUpload({ jobId }: JobFileUploadProps) {
    //const [files, setFiles] = useState<JobFile[]>([])
    const [isDragging, setIsDragging] = useState(false)
    const [editingFile, setEditingFile] = useState<JobFile | null>(null)
    const [newFileName, setNewFileName] = useState("")
    const [isUploading, setIsUploading] = useState(false)
    const { mutateAsync: uploadFile } = usePostFilesOwnerOwnerId()
    const { data: files } = useGetFilesOwnerFileIdPresign('job', jobId)
    const { mutateAsync: renameFile } = usePutFilesFileIdRename()
    const { mutateAsync: deleteFile } = useDeleteFilesOwnerFileId()
    const querryClient = useQueryClient()



    const processFiles = async (uploadedFiles: FileList | null) => {
        if (!uploadedFiles || uploadedFiles.length === 0) return;

        const filesArray = Array.from(uploadedFiles);

        setIsUploading(true);

        try {
            await Promise.all(
                filesArray.map(file =>
                    uploadFile({
                        owner: "job",
                        data: {
                            file,
                            name: file.name,
                        },
                        ownerId: jobId,
                    })
                )
            )
            toast.success("Files uploaded successfully")
        } catch (err) {
            toast.error("Error uploading files. Please try again.")
        } finally {
            querryClient.invalidateQueries({ queryKey: [`/files/job/${jobId}/presign`] })
            setIsUploading(false);
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        processFiles(e.target.files)
    }

    const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(true)
    }

    const handleDragLeave = (e: React.DragEvent<HTMLElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)
    }

    const handleDrop = (e: React.DragEvent<HTMLElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)
        processFiles(e.dataTransfer.files)
    }

    const handleRenameFile = async () => {
        if (!editingFile || !newFileName.trim()) return
        try {
            await renameFile({
                fileId: editingFile.id as string,
                data: {
                    fileName: newFileName.trim(),
                },
            })
            setEditingFile(null)
            setNewFileName("")
            toast.success("File renamed successfully")
        } catch (err) {
            toast.error("Error renaming file. Please try again.")
            return
        } finally {
            querryClient.invalidateQueries({ queryKey: [`/files/job/${jobId}/presign`] })
            setIsUploading(false);
        }
    }

    const handleDeleteFile = async (fileId: string) => {
        if (!fileId || !fileId.trim()) return
        try {
            await deleteFile({
                fileId: fileId,
                owner: 'job'
            })
            toast.success("File deleted successfully")
        } catch (err) {
            toast.error("Error deleting file. Please try again.")
            return
        } finally {
            querryClient.invalidateQueries({ queryKey: [`/files/job/${jobId}/presign`] })
            setIsUploading(false);
        }
    }

    const openRenameDialog = (file: JobFile) => {
        setEditingFile(file)
        setNewFileName(file.name)
    }

    return (
        <>
            <Card>
                <CardHeader>
                    <CardTitle>Job Files</CardTitle>
                    <CardDescription>Upload and manage files related to this job</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Upload Area */}
                    {/*
                        A real `<label>` holding an `sr-only` input, not an
                        `opacity-0` input stretched over the box. The invisible
                        input hands the click target to the platform and leaves
                        nothing on screen that reports itself as operable — a
                        keyboard user tabs into an unnamed file control sitting
                        over a decorative div. The label is the control, so it
                        is focusable, it is named, and the drop handlers stay on
                        it, which keeps dragging files in working.
                    */}
                    <label
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        className={cn(
                            "flex cursor-pointer flex-col items-center gap-2.5 rounded-[8px] border border-dashed px-5 py-8 text-center transition-colors",
                            isDragging
                                ? "border-primary bg-primary/[0.06]"
                                : "hover:bg-foreground/[0.02]",
                            isUploading && "pointer-events-none opacity-60",
                        )}
                    >
                        <Upload
                            className={cn(
                                "size-6",
                                isDragging ? "text-brand-ink" : "text-muted-foreground",
                            )}
                        />
                        <span className="text-[12.5px]">
                            {isDragging ? (
                                "Drop the files here"
                            ) : isUploading ? (
                                "Uploading…"
                            ) : (
                                <>
                                    Drag files here, or{" "}
                                    <span className="font-medium text-brand-ink">choose some</span>
                                </>
                            )}
                        </span>
                        <input
                            id={`job-file-upload-${jobId}`}
                            type="file"
                            multiple
                            aria-label="Choose files to upload"
                            onChange={handleFileUpload}
                            disabled={isUploading}
                            className="sr-only"
                        />
                    </label>

                    {/* Files List */}
                    {files?.urls && files.urls?.length > 0 && (
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Uploaded Files ({files.urls?.length})</Label>
                            <div className="space-y-2">
                                {files.urls.map((file) => (
                                    <div
                                        key={file.id}
                                        className="flex items-center justify-between p-3 bg-card border rounded-lg hover:bg-accent/50 transition-colors"
                                    >
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <FileText className="h-5 w-5 text-blue-600 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                {/* CLICKABLE FILE LINK */}
                                                <a
                                                    href={file.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm font-medium text-blue-600 hover:underline truncate block"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {file.name}
                                                </a>

                                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                    <span>{file.size_bytes}</span>
                                                    <span>•</span>
                                                    <span>{file.created_at}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => openRenameDialog(file)}
                                                className="h-8 w-8 p-0"
                                            >
                                                <Edit2 className="h-4 w-4" />
                                            </Button>

                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDeleteFile(file.id as string)}
                                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>

                                ))}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Rename Dialog */}
            <DialogShell
                open={!!editingFile}
                onOpenChange={() => setEditingFile(null)}
                title="Rename File"
                description="The extension is preserved."
                footer={
                    <>
                        <QuietAction size="md" onClick={() => setEditingFile(null)}>
                            Cancel
                        </QuietAction>
                        <PageAction
                            tone="primary"
                            onClick={handleRenameFile}
                            disabled={!newFileName.trim()}
                        >
                            Rename
                        </PageAction>
                    </>
                }
            >
                <FieldShell label="File name">
                    <input
                        id="file-name"
                        value={newFileName}
                        onChange={(e) => setNewFileName(e.target.value)}
                        placeholder="Enter file name"
                        aria-label="File name"
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                handleRenameFile()
                            }
                        }}
                        className={cn(FIELD_UNDERLINE, "pb-[7px] text-[12.5px] leading-[1.4]")}
                    />
                </FieldShell>
            </DialogShell>
        </>
    )
}
